/**
 * Pitch-canvas controller for /game-match-summary/<game_code> — React
 * migration Pass 11. Ports the template's canvas core from
 * app/templates/game_match_summary/index.html:
 *   - canvasState (~2027) and initializeCanvas/resize (~6902)
 *   - loadFrameData / processFetchedFrames double buffering (~6946-7115)
 *   - loadAllDataUnified single-flight unified loading (~6850-6900)
 *   - drawFrame + field/player/ball drawing (~7131-7546)
 *   - drawPotentialPasses + getPassArrowStyle (~6566-6825)
 *   - startCanvasPlayback video-driven rAF sync / stopCanvasPlayback (~7547-7621)
 *   - playMoment's canvas half: player highlighting + pass potential (~3913)
 *   - handleVideoPlay/Pause/Seeked canvas halves (~5593-5663)
 *   - team color pickers' apply/save logic (~6277-6390)
 *   - pass potential indicator (score rings, ~5219-5333)
 *   - console debug helpers (getCacheStats/toggleSyncDebug, ~2333-2360)
 *
 * One controller instance per page mount; created and destroyed by
 * GameMatchSummary. All DOM access is by the same element ids the template
 * used (the React tree renders those ids).
 *
 * Pass 12 wired in the pressing indicator + sparkline (pressingIndicator.ts,
 * called from preparePlayMoment and the playback loop) and the timeline
 * playback scrubber (updateTimelineScrubber, drawFrame ->
 * TimelineSection's #timeline-scrubber).
 *
 * Deliberately NOT here:
 *   - fetchPotentialPasses (~6544) — defined but never called in the
 *     template; dead code, not ported
 */
import { trackEvent } from '../../lib/analytics'
import { ScoreRing } from '../../lib/viz/scoreRing'
import {
  updatePressingIndicator,
  updatePressingIndicatorForFrame,
} from './pressingIndicator'
import { videoTimeToFrame, formatGameTime, type SyncState } from './frameTime'
import { TrackingDataCache, type TrackingFrame } from './trackingLoader'
import { BallTrailManager } from './ballTrail'
import { dotContrast } from './dotContrast'
import { BlockLinesManager, clampToPitch } from './blockLines'
import { hitTestPlayer, layerSubject, playerKey, toggleFocus,
         type HitTarget, type PlayerKey } from './playerFocus'
import { HeatMapManager } from './heatMap'
import { PlayerTrailsManager } from './playerTrails'
import { SpeedTrailsManager } from './speedTrails'
import { LOOKBACK_SECONDS, arrowLength, arrowStrokes, arrowVector } from './directionArrow'
import { SpatialControlManager } from './spatialControl'
import { FormationsManager } from './formations'
import { TelestrationController } from './telestration'
import { AnnotationController } from './annotations'
import type { GameData, Moment } from './types'
import { pathSeg } from '../../lib/urls'

declare global {
  interface Window {
    /** Console helpers, kept for parity with the template page. */
    getCacheStats?: () => unknown
    toggleSyncDebug?: (enabled?: boolean) => boolean
  }
}

/** Pass-potential payload as served per frame by /pass-potential. */
export interface PassPotentialData {
  frame: number
  moment_type?: string
  team_id?: number
  ball_holder?: { team: number; jersey: number; x: number; y: number } | null
  ball_position?: { x: number; y: number }
  attacking_direction?: string
  potential_passes?: {
    target_player?: { team: number; jersey: number; x: number; y: number }
    distance?: number
    angle_deg?: number
    pass_type?: string | null
    forward_progress?: number
    opponents_in_path?: number | null
    lines_broken?: number | null
    option_rank?: number | null
    score?: number
  }[]
  num_potential_passes?: number
}

/** Canvas state (template `canvasState`, from match_summary work). */
export interface CanvasState {
  canvas: HTMLCanvasElement | null
  ctx: CanvasRenderingContext2D | null
  frames: TrackingFrame[]
  currentFrame: number
  currentFrameIndex: number
  isPlaying: boolean
  animationId: number | null
  lastFrameTime: number
  isLoadingFrames: boolean
  pendingPlayback: boolean // playback requested during loading
  currentLoadFrame: number | null // frame currently loading (dedupe)
  highlightedPlayers: string[]
  highlightTimeout: ReturnType<typeof setTimeout> | null
  maxBufferSize: number // max frames in buffer (2 min at 30fps)
  // Potential passes visualization state
  potentialPasses: PassPotentialData | null
  currentPassMoment: Moment | null
  showPassOptions: boolean
  lastPassPotentialFrame: number | null
  // Unified loading
  unifiedLoadPromise: Promise<void> | null
  inUnifiedLoading: boolean
  // Playback bookkeeping
  lastFrameData: TrackingFrame | null
  currentPlayers: TrackingFrame['players'] | null
  lastDrawnFrame: number
  lastSyncLog: number
  debugSync: boolean
}

export interface CanvasControllerOptions {
  gameCode: string
  /** Space kept clear around the pitch, px. Defaults to the MARGIN_*
   *  constants, which clear the match summary's overlays; a host with
   *  no overlays (Match Lab) can draw the pitch nearly edge to edge. */
  fieldMargins?: { left: number; right: number; top: number; bottom: number }
  currentUserId: number
  getSync: () => SyncState
  getGameData: () => GameData | null
  getViewingAsTeam: () => number
  /** Pass 12: the selected press types (template state.currentPressTypes) —
   *  read by the pressing indicator's label when a pressing moment plays. */
  getPressTypes?: () => string[]
  /** Telestration layer/detection state changed (React mirrors button state). */
  onTelLayersChanged?: (layers: Record<string, boolean>, detectionActive: boolean,
    highlightMode?: 'off' | 'selecting' | 'active', highlightBall?: boolean,
    highlightLinesVideo?: boolean, highlightRingsSpatial?: boolean,
    highlightLinesSpatial?: boolean) => void
  /** Annotation mode entered/exited (React swaps moments panel <-> toolbar). */
  onAnnotationModeChange?: (active: boolean) => void
  /** Annotations list changed (React renders annotation cards). */
  onAnnotationsChange?: (annotations: unknown[]) => void
}

// MOCK SCORES FOR DEMO — CTO: replace with real scores from parquet.
// One definition, at module scope: it was declared twice with identical
// literals, once in drawPotentialPasses (the arrows) and once in the pass
// indicator (the rings), with a comment in each asking the reader to keep
// them equal by hand. The ring and the arrow for a pass now cannot disagree.
const MOCK_PASS_SCORES = [0.92, 0.68, 0.35, 0.85, 0.55, 0.42] as const
const MOCK_PASS_SCORE_FALLBACK = 0.5

/** The demo score for the nth pass option. The modulo makes every index land
 *  in range; naming the fallback is what lets the compiler agree. */
function mockPassScore(idx: number): number {
  return MOCK_PASS_SCORES[idx % MOCK_PASS_SCORES.length] ?? MOCK_PASS_SCORE_FALLBACK
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return { r, g, b }
}

function darken(rgb: { r: number; g: number; b: number }, factor: number) {
  return {
    r: Math.round(rgb.r * factor),
    g: Math.round(rgb.g * factor),
    b: Math.round(rgb.b * factor),
  }
}

/**
 * Get visual styling for pass arrows based on quality score.
 * Uses Priori performance colors: Teal (good), Amber (okay), Coral (bad).
 * Thresholds (same as score rings): >= 0.80 good, >= 0.50 okay, else bad.
 * A pass served with no score (momentScores.hasNoScore) is drawn in the
 * neutral grey (--state-neutral, dark theme), never as a bad pass.
 */
export function getPassArrowStyle(score: number | null) {
  if (score === null) {
    return {
      strokeColor: 'rgba(139, 150, 162, 0.85)', // Neutral #8B96A2
      fillColor: 'rgba(139, 150, 162, 0.95)',
      lineWidth: 2,
      arrowLength: 10,
    }
  }
  if (score >= 0.8) {
    // GOOD - Teal: slightly thicker, high opacity
    return {
      strokeColor: 'rgba(43, 217, 176, 0.95)', // Teal #2BD9B0
      fillColor: 'rgba(43, 217, 176, 1)',
      lineWidth: 2.5,
      arrowLength: 11,
    }
  } else if (score >= 0.5) {
    // OKAY - Amber: medium stroke, medium opacity
    return {
      strokeColor: 'rgba(244, 185, 66, 0.85)', // Amber #F4B942
      fillColor: 'rgba(244, 185, 66, 0.95)',
      lineWidth: 2,
      arrowLength: 10,
    }
  } else {
    // BAD - Coral: thinner stroke, lower opacity
    return {
      strokeColor: 'rgba(226, 85, 85, 0.70)', // Coral #E25555
      fillColor: 'rgba(226, 85, 85, 0.80)',
      lineWidth: 1.5,
      arrowLength: 9,
    }
  }
}

// Extract "team_jersey" (e.g. "0_16") from ids like "team0_player10" /
// "team1_jersey11" — template playMoment's extractTeamAndJersey.
function extractTeamAndJersey(playerId: string | number | null | undefined): string | null {
  if (!playerId) return null
  const playerStr = playerId.toString()

  let team: string | null = null
  let jersey: string | null = null

  if (playerStr.includes('team')) {
    const teamMatch = playerStr.match(/team(\d+)/)
    team = teamMatch?.[1] ?? null

    if (playerStr.includes('player')) {
      jersey = playerStr.match(/player(\d+)/)?.[1] ?? null
    } else if (playerStr.includes('jersey')) {
      jersey = playerStr.match(/jersey(\d+)/)?.[1] ?? null
    }
  }

  if (team !== null && jersey !== null) {
    return `${team}_${jersey}`
  }
  return null
}

export type PassPotentialStatus = 'loading' | 'ready' | 'none' | 'failed'

/** Waits before each retry of a failed pass-potential load. */
export const PASS_POTENTIAL_RETRY_MS = [2_000, 8_000, 30_000]

/**
 * Where to DRAW a ball whose tracked position is off the pitch: on the
 * nearest line (PRI-1926). x goes through blockLines' clampToPitch, the
 * existing length clamp; y is the same rule across the width. The served
 * frame is never changed — only the drawn dot and its trail.
 */
export function clampBallToPitch(x: number, y: number): { x: number; y: number } {
  // Half the width in tracking units (centimetres from the centre spot),
  // from the controller's own pitch size; read at call time because the
  // class is declared below.
  const w = (CanvasController.PITCH_WID_M / 2) * 100
  return {
    x: clampToPitch(x),
    y: y < -w ? -w : y > w ? w : y,
  }
}

export class CanvasController {
  readonly gameCode: string
  readonly opts: CanvasControllerOptions

  state: CanvasState = {
    canvas: null,
    ctx: null,
    frames: [],
    currentFrame: 0,
    currentFrameIndex: 0,
    isPlaying: false,
    animationId: null,
    lastFrameTime: 0,
    isLoadingFrames: false,
    pendingPlayback: false,
    currentLoadFrame: null,
    highlightedPlayers: [],
    highlightTimeout: null,
    maxBufferSize: 3600,
    potentialPasses: null,
    currentPassMoment: null,
    showPassOptions: false,
    lastPassPotentialFrame: null,
    unifiedLoadPromise: null,
    inUnifiedLoading: false,
    lastFrameData: null,
    currentPlayers: null,
    lastDrawnFrame: -1,
    lastSyncLog: 0,
    debugSync: false,
  }

  /** Template syncState.videoPlaying (the rest of SyncState lives in React). */
  videoPlaying = false
  /** Template state.passPotentialData — pass potential indexed by frame. */
  passPotentialData: Record<number, PassPotentialData> = {}
  /** Where the pass-potential load stands. 'none' is the server saying
   *  the game has no pass data (404, or an empty list); 'failed' is a
   *  load that kept erroring after its retries. The panels say which,
   *  instead of "no passes yet" for the whole match (PRI-1826). */
  passPotentialStatus: PassPotentialStatus = 'loading'
  /** Told when passPotentialStatus moves — it is not React state. */
  onPassPotentialStatus: ((s: PassPotentialStatus) => void) | null = null
  /** Template window._hasTelestration. */
  hasTelestration = false
  /** Previous fix per player (team_jersey -> cm position), rebuilt each
   *  drawn frame for the direction arrows. */
  private prevPlayerPos = new Map<string, { x: number; y: number }>()
  private arrowDtSeconds = LOOKBACK_SECONDS

  /** Players highlighted by the detection overlay (team_jersey strings).
   *  Set by `TelestrationController.syncHighlightToSpatial` every frame
   *  the detection boxes are drawn, cleared when the highlight ends. */
  detectionHighlightPlayers: string[] = []
  /** Whether to draw gold rings around detection-highlighted players. */
  detectionHighlightRings = true
  /** Whether to draw connecting lines between detection-highlighted players. */
  detectionHighlightLines = true

  trackingCache = new TrackingDataCache()
  spatialControl: SpatialControlManager
  heatMap: HeatMapManager
  ballTrail: BallTrailManager
  playerTrails: PlayerTrailsManager
  speedTrails: SpeedTrailsManager
  blockLines: BlockLinesManager
  formations: FormationsManager
  telestration: TelestrationController
  annotations: AnnotationController

  private resizeObserver: ResizeObserver | null = null
  private _teamColors: Record<number, string> | null = null
  private passPotentialIndicatorTimer: ReturnType<typeof setTimeout> | null = null
  private destroyed = false
  /** The video the play/seek handlers were handed. Looked up by id instead,
   *  a popped-out video (it lives in the pop-out window's document) was not
   *  found, and the pitch froze while it played. */
  private video: HTMLVideoElement | null = null

  /** The match video, wherever it lives: the one the handlers were given,
   *  else #game-video on this page, else in the pop-out window (a video
   *  popped out before any play or seek). The one lookup every tool on the
   *  controller uses — annotations and telestration looked it up by id on
   *  this page and lost it once it was popped out. */
  videoEl(): HTMLVideoElement | null {
    const byId = (d: Document | undefined) =>
      (d?.getElementById('game-video') as HTMLVideoElement | null) ?? null
    const pip = (window as unknown as {
      documentPictureInPicture?: { window?: Window | null }
    }).documentPictureInPicture?.window
    return this.video ?? byId(document) ?? byId(pip?.document)
  }
  /** Whose block the Block & Lines layer draws; set from the page. */
  blockTeam = 0
  /** Focused players ("team_jersey"). Empty = the whole team, as before. */
  focusPlayers: PlayerKey[] = []
  /** Called when a click changes the focus set, so the page can re-render. */
  onFocusChange: ((focus: PlayerKey[]) => void) | null = null
  /** Where each player was last drawn, in canvas pixels — the hit-test map. */
  private hitTargets: HitTarget[] = []

  constructor(opts: CanvasControllerOptions) {
    this.gameCode = opts.gameCode
    this.opts = opts

    // Template initializeSpatialControl / initializeFormations (~2116-2133)
    this.spatialControl = new SpatialControlManager({
      getGameCode: () => this.gameCode,
      getCanvasState: () => this.state,
      onRedraw: (frameData) => this.drawFrame(frameData as TrackingFrame),
      // The heatmap must land on the SAME rectangle the pitch is drawn in.
      // It recomputed its own margin box before, which is how it came to
      // bleed past the touchlines once the pitch was letterboxed.
      getFieldRect: () => this.fieldRect(),
      // Grids are computed from the tracking cache when it has the frame
      // (spatialCompute.ts); these two accessors are that supply line.
      getTrackingFrame: (frame) => this.trackingCache.getFrameData(frame),
      ensureTrackingFrame: (frame) => this.trackingCache.prioritizeRange(frame),
    })
    this.spatialControl.config.useLocalSource = false // Use R2 parquet files

    // Reads the frame buffer this controller already holds — no fetch.
    this.heatMap = new HeatMapManager({
      getCanvasState: () => this.state,
      getFieldRect: () => this.fieldRect(),
    })

    this.ballTrail = new BallTrailManager({
      getGameCode: () => this.gameCode,
      getCanvasState: () => this.state,
      // The trail is the ball's path, so it stays on the pitch like the ball
      // itself (PRI-1926).
      convertToCanvasCoords: (x, y) => {
        const c = clampBallToPitch(x, y)
        return this.convertToCanvasCoords(c.x, c.y)
      },
      getTeamColors: () => this.getTeamColors(),
    })

    this.playerTrails = new PlayerTrailsManager({
      getCanvasState: () => this.focusedState(),
      convertToCanvasCoords: (x, y) => this.convertToCanvasCoords(x, y),
      getTeamColors: () => this.getTeamColors(),
    })

    this.speedTrails = new SpeedTrailsManager({
      getCanvasState: () => this.focusedState(),
      convertToCanvasCoords: (x, y) => this.convertToCanvasCoords(x, y),
    })

    this.blockLines = new BlockLinesManager({
      getCanvasState: () => this.state,
      convertToCanvasCoords: (x, y) => this.convertToCanvasCoords(x, y),
      getTeamColors: () => this.getTeamColors(),
      getTeam: () => this.blockTeam,
    })

    this.formations = new FormationsManager({
      getGameCode: () => this.gameCode,
      getCanvasState: () => this.state,
      convertToCanvasCoords: (x, y) => this.convertToCanvasCoords(x, y),
    })
    this.formations.config.debug = true // Set to true for verbose logging

    this.telestration = new TelestrationController(this)
    this.annotations = new AnnotationController(this)
  }

  getSync(): SyncState {
    return this.opts.getSync()
  }

  /**
   * Template DOMContentLoaded canvas block (~2393-2427): initialize canvas,
   * managers, first frame. Called from the page mount effect.
   */
  init(): void {
    this.initializeCanvas()
    this.telestration.init()
    this.annotations.init()
    // Load first frame to show tracking data
    void this.loadFrameData(0, false)

    // Console helpers (template ~2333-2360)
    window.getCacheStats = () => this.trackingCache.getStats()
    window.toggleSyncDebug = (enabled?: boolean) => {
      this.state.debugSync = enabled !== false
      return this.state.debugSync
    }
  }

  destroy(): void {
    this.destroyed = true
    this.stopCanvasPlayback()
    this.video = null
    if (this.state.highlightTimeout) clearTimeout(this.state.highlightTimeout)
    if (this.passPotentialIndicatorTimer) clearTimeout(this.passPotentialIndicatorTimer)
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.telestration.destroy()
    this.annotations.destroy()
    this.spatialControl.destroy()
    this.trackingCache.destroy()
    delete window.getCacheStats
    delete window.toggleSyncDebug
  }

  // -------------------------------------------------------------------------
  // Canvas init + resize (template initializeCanvas, ~6902)
  // -------------------------------------------------------------------------
  initializeCanvas(): void {
    const canvas = document.getElementById('spatial-canvas') as HTMLCanvasElement | null
    if (!canvas) {
      console.error('Canvas element not found')
      return
    }

    const ctx = canvas.getContext('2d')
    const container = canvas.parentElement!

    const resizeCanvas = () => {
      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight

      // Only resize if dimensions changed
      if (canvas.width !== containerWidth || canvas.height !== containerHeight) {
        canvas.width = containerWidth
        canvas.height = containerHeight

        // Redraw current frame after resize
        if (
          this.state.frames &&
          this.state.frames.length > 0 &&
          this.state.currentFrameIndex < this.state.frames.length
        ) {
          this.redrawCurrentFrame()
        }
      }
    }

    // Set initial size
    resizeCanvas()

    this.state.canvas = canvas
    this.state.ctx = ctx

    // Watch for container size changes
    this.resizeObserver = new ResizeObserver(() => resizeCanvas())
    this.resizeObserver.observe(container)
  }

  // -------------------------------------------------------------------------
  // Frame loading (template processFetchedFrames + loadFrameData)
  // -------------------------------------------------------------------------

  /** Process fetched frames with double buffering logic. */
  private processFetchedFrames(data: TrackingFrame[], loadForPlayback: boolean): void {
    if (!Array.isArray(data) || data.length === 0) {
      console.warn('No frames to process')
      return
    }

    const firstIncoming = data[0]
    if (firstIncoming === undefined) return
    const actualFirstFrame = firstIncoming.frame
    const expectedNextFrame = this.state.currentFrame + this.state.frames.length

    // DOUBLE BUFFERING: Append if sequential, replace if seeking
    if (this.state.frames.length > 0 && actualFirstFrame === expectedNextFrame && loadForPlayback) {
      // Sequential playback - append new frames
      this.state.frames.push(...data)

      // Trim buffer if it exceeds max size (keep last N frames)
      if (this.state.frames.length > this.state.maxBufferSize) {
        const trimCount = this.state.frames.length - this.state.maxBufferSize
        this.state.frames.splice(0, trimCount)
        this.state.currentFrameIndex = Math.max(0, this.state.currentFrameIndex - trimCount)
        const oldest = this.state.frames[0]
        if (oldest) this.state.currentFrame = oldest.frame
      }
    } else {
      // Seeking or initial load - replace entire buffer
      this.state.frames = data
      this.state.currentFrame = actualFirstFrame

      // Calculate the correct index based on context
      if (loadForPlayback) {
        const video = this.videoEl()
        if (video && video.src) {
          const currentVideoFrame = videoTimeToFrame(this.getSync(), video.currentTime)
          this.state.currentFrameIndex = Math.max(
            0,
            Math.min(currentVideoFrame - actualFirstFrame, data.length - 1),
          )
        } else {
          this.state.currentFrameIndex = 0
        }
      } else {
        this.state.currentFrameIndex = 0
      }
    }

    // Skip draw and playback start during unified loading
    // (unified loading will handle drawing after ALL data is ready)
    if (!this.state.inUnifiedLoading) {
      // Draw the current frame
      if (this.state.frames[this.state.currentFrameIndex]) {
        this.redrawCurrentFrame()
      }

      // Start playback if needed
      if (loadForPlayback && !this.state.isPlaying) {
        this.startCanvasPlayback()
      }

      // Handle any pending playback request
      if (this.state.pendingPlayback) {
        this.state.pendingPlayback = false
        if (this.videoPlaying && !this.state.isPlaying) {
          this.startCanvasPlayback()
        }
      }
    }
  }

  /** Load frame data — WASM tracking cache first, /workbench/frames fallback. */
  async loadFrameData(frameNumber: number, loadForPlayback: boolean = false): Promise<void> {
    // TRACKING CACHE: Check if we can serve from WASM cache
    const frameCount = loadForPlayback ? 1800 : 1
    const canUseCache = this.trackingCache.isInitialLoaded || this.trackingCache.isFullyLoaded

    if (canUseCache) {
      // Check if all requested frames are in cache
      let allFramesCached = true
      for (let i = 0; i < frameCount; i++) {
        if (!this.trackingCache.hasCachedFrame(frameNumber + i)) {
          allFramesCached = false
          break
        }
      }

      if (allFramesCached) {
        // Build frame array from cache
        const cachedFrames: TrackingFrame[] = []
        for (let i = 0; i < frameCount; i++) {
          cachedFrames.push(this.trackingCache.getFrameData(frameNumber + i)!)
        }

        this.processFetchedFrames(cachedFrames, loadForPlayback)
        return Promise.resolve()
      }
    }

    // If WASM is initialized but background loading hasn't reached this
    // range, load the parquet chunk on demand so we stay on the efficient
    // Arrow path instead of falling back to the JSON endpoint.
    if (this.trackingCache.parquetWasm && this.trackingCache.gameCode) {
      await this.trackingCache.prioritizeRange(frameNumber)
      // Re-check after priority load
      let allNowCached = true
      for (let i = 0; i < frameCount; i++) {
        if (!this.trackingCache.hasCachedFrame(frameNumber + i)) {
          allNowCached = false
          break
        }
      }
      if (allNowCached) {
        const cachedFrames: TrackingFrame[] = []
        for (let i = 0; i < frameCount; i++) {
          cachedFrames.push(this.trackingCache.getFrameData(frameNumber + i)!)
        }
        this.processFetchedFrames(cachedFrames, loadForPlayback)
        return Promise.resolve()
      }
    }

    // Check if already loading this exact frame range
    if (this.state.isLoadingFrames && this.state.currentLoadFrame === frameNumber) {
      return Promise.resolve()
    }

    if (!this.state.canvas) {
      console.error('[Frames] ERROR: Canvas not initialized')
      return Promise.reject(new Error('Canvas not initialized'))
    }

    this.state.isLoadingFrames = true
    this.state.currentLoadFrame = frameNumber

    // JSON fallback — only reached when WASM is unavailable or the parquet
    // endpoint doesn't cover this range.
    const url = `/api/v1/workbench/frames/${frameNumber}/${frameCount}?game=${pathSeg(this.gameCode)}`

    try {
      const response = await fetch(url, { credentials: 'same-origin' })
      const data = await response.json()

      // Workbench endpoint returns a bare array, not {success: true, frames: [...]}
      if (Array.isArray(data) && data.length > 0) {
        this.processFetchedFrames(data as TrackingFrame[], loadForPlayback)
      } else {
        console.warn('No frames returned or invalid response:', data)
      }
    } catch (error) {
      console.error('Error loading frame data:', error)
    } finally {
      this.state.isLoadingFrames = false
      this.state.currentLoadFrame = null

      // Ensure animation loop is running if we should be playing.
      // This handles cases where the loop was broken or never started
      // (template's index-driven fallback loop inside loadFrameData).
      if (this.state.isPlaying && !this.state.animationId) {
        this.state.lastFrameTime = performance.now()
        const animate = (currentTime: number) => {
          if (!this.state.isPlaying || this.destroyed) return
          const elapsed = currentTime - this.state.lastFrameTime
          if (elapsed >= 33) {
            this.state.currentFrameIndex++
            if (this.state.currentFrameIndex >= this.state.frames.length) {
              if (!this.state.isLoadingFrames) {
                const nextFrame = this.state.currentFrame + this.state.frames.length
                void this.loadFrameData(nextFrame, true)
              }
              this.state.currentFrameIndex = this.state.frames.length - 1
            } else {
              try {
                this.redrawCurrentFrame()
              } catch (err) {
                console.error('Error in drawFrame:', err)
              }
            }
            this.state.lastFrameTime = currentTime
          }
          this.state.animationId = requestAnimationFrame(animate)
        }
        this.state.animationId = requestAnimationFrame(animate)
      }

      // Clear pending playback flag
      if (this.state.pendingPlayback) {
        this.state.pendingPlayback = false
      }
    }

    return Promise.resolve()
  }

  // -------------------------------------------------------------------------
  // Unified loading (template loadAllDataUnified, ~6850)
  // -------------------------------------------------------------------------

  showSpatialLoadingIndicator(): void {
    const overlay = document.getElementById('spatial-loading-overlay')
    if (overlay) overlay.style.display = 'flex'
  }

  hideSpatialLoadingIndicator(): void {
    const overlay = document.getElementById('spatial-loading-overlay')
    if (overlay) overlay.style.display = 'none'
  }

  /**
   * Ensures all required data (tracking, spatial, formations) is loaded before
   * proceeding. Single-flight: a moment click and the seeked event it triggers
   * can both request a unified load for the same position; join the in-flight
   * load instead of clobbering shared state.
   */
  async loadAllDataUnified(frame: number, loadForPlayback: boolean = false): Promise<void> {
    if (this.state.unifiedLoadPromise) {
      return this.state.unifiedLoadPromise
    }
    this.state.unifiedLoadPromise = this._loadAllDataUnifiedImpl(frame, loadForPlayback)
    try {
      return await this.state.unifiedLoadPromise
    } finally {
      this.state.unifiedLoadPromise = null
    }
  }

  private async _loadAllDataUnifiedImpl(frame: number, loadForPlayback: boolean): Promise<void> {
    this.showSpatialLoadingIndicator()

    // Set flag to prevent premature drawing during parallel loading
    this.state.inUnifiedLoading = true

    try {
      // Initialize canvas if needed
      if (!this.state.canvas) {
        this.initializeCanvas()
      }

      // Load ALL required data in parallel
      await Promise.all([
        this.loadFrameData(frame, loadForPlayback), // Tracking data
        this.spatialControl.ensureLoaded(frame), // Spatial control (if enabled)
        this.formations.ensureLoaded(frame), // Formations (if enabled)
      ])

      // NOW draw after all data is ready
      if (this.state.frames && this.state.frames[this.state.currentFrameIndex]) {
        this.redrawCurrentFrame()
      }
    } catch (error) {
      console.error('[UnifiedLoading] Error loading data:', error)
      console.warn('[UnifiedLoading] Some data may be missing or out of sync')
      throw error
    } finally {
      this.state.inUnifiedLoading = false
      this.hideSpatialLoadingIndicator()
    }
  }

  // -------------------------------------------------------------------------
  // Drawing (template drawFrame + helpers)
  // -------------------------------------------------------------------------

  /** Update match clock display (template updateMatchClock, ~7116). */
  updateMatchClock(frame: number): void {
    const timeString = formatGameTime(frame, this.getSync().fps)
    const videoClockEl = document.getElementById('video-match-clock')
    if (videoClockEl) {
      videoClockEl.textContent = timeString
    }

    const videoFrameEl = document.getElementById('video-match-frame')
    if (videoFrameEl) {
      videoFrameEl.textContent = `f${frame}`
    }
    // spatial-match-frame removed (2026-09-06): SpatialFrameChip in the
    // topbar owns the pitch frame counter via React state.
  }

  /** The frame the scrubber is on, or undefined before tracking has loaded. */
  currentFrame(): TrackingFrame | undefined {
    return this.state.frames?.[this.state.currentFrameIndex]
  }

  /** Redraw whatever frame the scrubber is on.
   *
   *  Twelve places said `drawFrame(this.state.frames[this.state.currentFrameIndex])`
   *  verbatim. One method indexes once, so "there is no frame yet" is decided
   *  in a single place instead of twelve — and the toggles that call it no
   *  longer need to know how frames are stored. */
  redrawCurrentFrame(): void {
    const frame = this.currentFrame()
    if (frame) this.drawFrame(frame)
  }

  /** Draw a single frame. */
  drawFrame(frameData: TrackingFrame): void {
    const ctx = this.state.ctx
    const canvas = this.state.canvas

    if (!ctx || !canvas) {
      console.error('Canvas context not available')
      return
    }

    // Hide placeholder once we start drawing
    const placeholder = document.getElementById('spatial-placeholder')
    if (placeholder) {
      placeholder.style.display = 'none'
    }

    // Store for later redraws (e.g. color picker changes)
    this.state.lastFrameData = frameData

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Draw field background (soft gray pitch)
    this.drawFieldBackground()

    // Draw spatial control overlay (after background, before field lines)
    if (this.spatialControl.enabled) {
      this.spatialControl.draw(frameData.frame)
    }

    // Same slot as spatial control, and for the same reason: over the
    // background, under the lines, so the pitch stays legible through it.
    this.heatMap.draw()

    // Draw field lines ON TOP of spatial control (so lines are visible)
    this.drawFieldLines()

    // Draw formations overlay (connecting lines for tactical groups).
    // Pass frameData so line positions come from the exact frame being drawn,
    // not the currentFrameIndex indirection (stale during playback).
    if (this.formations.enabled) {
      this.formations.draw(frameData.frame, frameData)
    }

    // Under the player markers on purpose: the dot is "now", the trail is
    // only where they came from, and over the top it hides the marker.
    this.blockLines.draw()
    this.playerTrails.draw()
    this.speedTrails.draw()

    // Rebuilt every frame: players move, so last frame's targets are stale.
    this.hitTargets = []

    // Draw players. Direction arrows measure against the cached frame
    // LOOKBACK ago — one cache read per frame, matched by team+jersey.
    if (frameData.players) {
      this.state.currentPlayers = frameData.players
      const fps = this.getSync().fps
      const lookback = Math.max(1, Math.round(fps * LOOKBACK_SECONDS))
      const prevFrame = this.trackingCache.getFrameData(frameData.frame - lookback)
      this.prevPlayerPos.clear()
      if (prevFrame?.players) {
        for (const p of prevFrame.players) {
          if (p.jersey == null) continue
          this.prevPlayerPos.set(`${p.team}_${p.jersey}`, { x: p.x, y: p.y })
        }
      }
      this.arrowDtSeconds = lookback / fps
      frameData.players.forEach((player) => {
        this.drawPlayer(player)
      })
    }

    // Draw connecting lines between detection-highlighted players on the
    // pitch so the spatial shape matches the video overlay.
    this.drawDetectionHighlightLines(frameData)

    // Over the players on purpose: under them the trail vanishes into the
    // cluster that is always around the ball.
    this.ballTrail.draw()

    // Draw potential passes overlay (dashed arrows to alternative receivers).
    // Only trigger indicator once per unique frame (prevents re-trigger spam
    // during playback).
    if (this.state.showPassOptions) {
      const potentialData: PassPotentialData | null =
        this.passPotentialData[frameData.frame] || null

      if (potentialData && this.state.lastPassPotentialFrame !== frameData.frame) {
        this.state.potentialPasses = potentialData
        this.state.lastPassPotentialFrame = frameData.frame
        // Show score ring indicator (auto-hides after 5 seconds along with arrows)
        this.updatePassPotentialIndicator(null, potentialData)
      }
      // Don't clear potentialPasses here - playMoment/hidePassPotentialIndicator control timing
    }
    this.drawPotentialPasses(frameData)

    // Draw ball
    if (frameData.ball) {
      this.drawBall(frameData.ball)
    }

    // Update match clock
    this.updateMatchClock(frameData.frame)

    // Update timeline scrubber position (Pass 12)
    this.updateTimelineScrubber(frameData.frame)
  }

  /**
   * Move the timeline playback scrubber (template updateTimelineScrubber,
   * ~4952 — it kept a JS reference in state.timelineScrubber; the React
   * TimelineSection renders the one scrubber with this id). Pass 12.
   */
  private updateTimelineScrubber(currentFrame: number): void {
    const scrubber = document.getElementById('timeline-scrubber')
    if (!scrubber) return
    const totalFrames = this.opts.getGameData()?.total_frames || 54000
    const percentage = currentFrame / totalFrames
    scrubber.style.left = `${percentage * 100}%`
  }

  /** Convert tracking coordinates (cm) to canvas pixels. */
  /**
   * The pitch rectangle in canvas pixels, and the single metres→pixels scale
   * used for BOTH axes.
   *
   * Rewritten 2026-08-25. The previous mapping stretched x and y
   * independently to fill a 16:9 pane: at a 1080px pane that made a metre
   * 8.66px across and 7.05px down — a 23% horizontal stretch — while
   * drawFieldLines drew its markings from magic fractions of the canvas
   * (penalty box 17.3m deep instead of 16.5m, 44.9m wide instead of 40.3m)
   * and the centre circle as a true circle. Lines and dots therefore
   * described two different pitches, and every shape judgement this view
   * exists to support — compactness, width, distance between players — was
   * read off a distorted picture.
   *
   * One uniform scale, letterboxed inside the pane, is the fix. Everything
   * that draws goes through here (players, ball, formations, pass options,
   * telestration, and the spatial-control heatmap via fieldRect), so the
   * furniture and the data cannot drift apart again.
   */
  static readonly PITCH_LEN_M = 105
  static readonly PITCH_WID_M = 68
  /** Room outside the touchlines for players who step off the field. */
  private static readonly OFF_FIELD_BUFFER = 1.06

  /** The off-field band, in canvas pixels — one value per SIDE.
   *
   *  It began as a single 40 on all four, which spent 80px of the one
   *  dimension that limits how large the pitch can be drawn AND put the
   *  drawing underneath the chrome that overlays the same canvas. Each side
   *  now buys exactly what sits on it:
   *
   *    LEFT   the layer stack (124px wide at left: 8)
   *    TOP    the formation labels
   *    RIGHT  the pop-out button
   *    BOTTOM nothing — only what the pitch needs to breathe
   *
   *  Symmetric now that the layer controls moved to the topbar dropdown
   *  (2026-09-06). The former 144px left margin was clearing the left-edge
   *  column of toggles; that column is gone. */
  static readonly MARGIN_LEFT = 48
  static readonly MARGIN_RIGHT = 48
  static readonly MARGIN_TOP = 38
  static readonly MARGIN_BOTTOM = 12

  fieldRect(): { x: number; y: number; w: number; h: number; scale: number } {
    const canvas = this.state.canvas!
    // Symmetric margins (2026-09-06): the left-edge layer column is gone.
    // The container is 16:9 (1.78) and the pitch plus off-field buffer is
    // 1.54, so the drawing is HEIGHT-limited on any normal pane: vertical
    // margin costs size directly, horizontal margin mostly costs empty band.
    const m = this.opts?.fieldMargins ?? {
      left: CanvasController.MARGIN_LEFT, right: CanvasController.MARGIN_RIGHT,
      top: CanvasController.MARGIN_TOP, bottom: CanvasController.MARGIN_BOTTOM }
    const drawableW = Math.max(1, canvas.width - m.left - m.right)
    const drawableH = Math.max(1, canvas.height - m.top - m.bottom)
    const rangeX = CanvasController.PITCH_LEN_M * CanvasController.OFF_FIELD_BUFFER
    const rangeY = CanvasController.PITCH_WID_M * CanvasController.OFF_FIELD_BUFFER
    // One scale, chosen so the whole pitch (plus its buffer) fits.
    const scale = Math.min(drawableW / rangeX, drawableH / rangeY)
    const w = CanvasController.PITCH_LEN_M * scale
    const h = CanvasController.PITCH_WID_M * scale
    return {
      x: m.left + (drawableW - w) / 2,
      y: m.top + (drawableH - h) / 2,
      w,
      h,
      scale,
    }
  }

  convertToCanvasCoords(xCm: number, yCm: number): { x: number; y: number } {
    const f = this.fieldRect()
    // Tracking x/y are centimetres from the centre spot; y is up-positive
    // on the pitch and down-positive on a canvas, hence the negation.
    return {
      x: f.x + f.w / 2 + (xCm / 100) * f.scale,
      y: f.y + f.h / 2 - (yCm / 100) * f.scale,
    }
  }

  /** Draw field background only (light pitch green — founder ask 2026-08-25,
      replacing the clinical soft gray). */
  private drawFieldBackground(): void {
    const { ctx, canvas } = this.state
    if (!ctx || !canvas) return

    // Figure and ground. The whole canvas used to be pitch green, so the
    // surround read as more field and the touchline carried the boundary
    // alone. The pitch is now the only green shape on a dark ground, which
    // is also the band the overlays sit in.
    ctx.fillStyle = '#0E1116'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    const f = this.fieldRect()
    if (f.w <= 0 || f.h <= 0) return
    ctx.fillStyle = '#DFEEDD'
    ctx.fillRect(f.x, f.y, f.w, f.h)
  }

  /**
   * Field markings, every one of them from the real dimensions of a pitch
   * (Laws of the Game) mapped through the same scale the players use.
   * They were fractions of the canvas before, which described a different
   * pitch from the one the dots were plotted on.
   */
  private drawFieldLines(): void {
    const { ctx, canvas } = this.state
    if (!ctx || !canvas) return
    if (canvas.width < 120 || canvas.height < 120) return   // mid-resize

    const f = this.fieldRect()
    if (f.w <= 0 || f.h <= 0) return
    const m = (metres: number) => metres * f.scale     // metres -> pixels
    const cx = f.x + f.w / 2
    const cy = f.y + f.h / 2

    // Markings are reference, not content: they sit behind the players in
    // weight as well as in z-order.
    ctx.strokeStyle = 'rgba(91, 100, 114, 0.55)'
    ctx.lineWidth = Math.max(1, f.scale * 0.12)   // ~12cm, the real line width
    ctx.lineJoin = 'miter'

    ctx.strokeRect(f.x, f.y, f.w, f.h)            // touchlines + goal lines

    ctx.beginPath()                                // halfway line
    ctx.moveTo(cx, f.y)
    ctx.lineTo(cx, f.y + f.h)
    ctx.stroke()

    ctx.beginPath()                                // centre circle, r = 9.15m
    ctx.arc(cx, cy, m(9.15), 0, Math.PI * 2)
    ctx.stroke()

    ctx.fillStyle = 'rgba(91, 100, 114, 0.55)'
    ctx.beginPath()                                // centre spot
    ctx.arc(cx, cy, Math.max(1.5, m(0.2)), 0, Math.PI * 2)
    ctx.fill()

    // Penalty area: 16.5m deep, 40.32m across. Goal area: 5.5m by 18.32m.
    // Penalty spot 11m from the goal line; the arc is the part of the 9.15m
    // circle around the spot that falls outside the box.
    const boxes: [number, number][] = [[f.x, 1], [f.x + f.w, -1]]
    for (const [goalLineX, dir] of boxes) {
      ctx.strokeRect(goalLineX, cy - m(40.32) / 2, dir * m(16.5), m(40.32))
      ctx.strokeRect(goalLineX, cy - m(18.32) / 2, dir * m(5.5), m(18.32))

      const spotX = goalLineX + dir * m(11)
      ctx.beginPath()
      ctx.arc(spotX, cy, Math.max(1.5, m(0.2)), 0, Math.PI * 2)
      ctx.fill()

      // D-arc: sweep only the segment beyond the 16.5m line.
      const theta = Math.acos(Math.min(1, m(5.5) / m(9.15)))
      ctx.beginPath()
      if (dir === 1) ctx.arc(spotX, cy, m(9.15), -theta, theta)
      else ctx.arc(spotX, cy, m(9.15), Math.PI - theta, Math.PI + theta)
      ctx.stroke()
    }

    // Corner arcs, 1m radius, quarter circles into the field.
    const corners: [number, number, number][] = [
      [f.x, f.y, 0], [f.x + f.w, f.y, Math.PI / 2],
      [f.x + f.w, f.y + f.h, Math.PI], [f.x, f.y + f.h, -Math.PI / 2],
    ]
    for (const [x, y, from] of corners) {
      ctx.beginPath()
      ctx.arc(x, y, m(1), from, from + Math.PI / 2)
      ctx.stroke()
    }
  }

  /**
   * The canvas state as a movement layer should see it: identical, except the
   * selection is the FOCUS set when one exists. Layers already narrow to
   * `highlightedPlayers`, so focus rides the mechanism that is already there
   * rather than adding a second one to every layer.
   */
  private focusedState() {
    return {
      ...this.state,
      highlightedPlayers: layerSubject(this.focusPlayers, this.state.highlightedPlayers ?? []),
    }
  }

  /** Focus a player, or unfocus them if already focused. */
  toggleFocusPlayer(key: PlayerKey): void {
    this.focusPlayers = toggleFocus(this.focusPlayers, key)
    this.heatMap.only = this.focusPlayers
    this.heatMap.invalidate()
    this.onFocusChange?.(this.focusPlayers)
    this.redrawCurrent()
  }

  clearFocus(): void {
    if (!this.focusPlayers.length) return
    this.focusPlayers = []
    this.heatMap.only = []
    this.heatMap.invalidate()
    this.onFocusChange?.(this.focusPlayers)
    this.redrawCurrent()
  }

  /** Redraw the frame on screen, if there is one. */
  redrawCurrent(): void {
    if (this.state.frames?.length && this.state.currentFrameIndex < this.state.frames.length) {
      this.redrawCurrentFrame()
    }
  }

  /** Click on the pitch: focus the player under the cursor, if any. */
  handlePitchClick(clientX: number, clientY: number): void {
    const canvas = this.state.canvas
    if (!canvas) return
    const r = canvas.getBoundingClientRect()
    // The canvas backing store is larger than its CSS box; hit targets were
    // recorded in backing-store pixels, so the click has to be scaled too.
    const x = (clientX - r.left) * (canvas.width / r.width)
    const y = (clientY - r.top) * (canvas.height / r.height)
    const hit = hitTestPlayer(this.hitTargets, x, y)
    if (hit) this.toggleFocusPlayer(hit.key)
  }

  /** Team colors from CSS custom properties (cached). */
  getTeamColors(): Record<number, string> {
    if (!this._teamColors) {
      const style = getComputedStyle(document.documentElement)
      this._teamColors = {
        0: style.getPropertyValue('--spatial-control-blue').trim(),
        1: style.getPropertyValue('--spatial-control-red').trim(),
      }
    }
    return this._teamColors
  }

  /** Draw player. */
  private drawPlayer(player: TrackingFrame['players'][number]): void {
    const ctx = this.state.ctx!

    // API returns 'team' not 'team_id'
    const teamId = player.team

    // Skip non-player entities (referees are team 3/4)
    if (teamId !== 0 && teamId !== 1) return

    const pos = this.convertToCanvasCoords(player.x, player.y)
    const color = this.getTeamColors()[teamId] || '#999'

    if (player.jersey != null) {
      this.hitTargets.push({
        key: playerKey(teamId, player.jersey),
        team: teamId, jersey: player.jersey, x: pos.x, y: pos.y,
      })
    }

    // Focus mode: the players NOT being studied stay on the pitch as small
    // team-coloured dots. Removing them entirely would answer "where did he
    // run" while destroying "why" — a run only means anything against the
    // shape around it. The ball is never dimmed.
    if (this.focusPlayers.length && player.jersey != null
        && !this.focusPlayers.includes(playerKey(teamId, player.jersey))) {
      ctx.save()
      ctx.globalAlpha = 0.55
      ctx.beginPath()
      ctx.arc(pos.x, pos.y, 3.5, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()
      ctx.restore()
      return
    }

    // Check if this player should be highlighted.
    // highlightedPlayers contains "team_jersey" format (e.g., "0_16").
    // detectionHighlightPlayers carries the same format, bridged from
    // the video overlay's detection highlight.
    const jerseyStr = player.jersey != null ? player.jersey.toString() : null
    const teamJerseyStr = jerseyStr ? `${teamId}_${jerseyStr}` : null
    const isHighlighted =
      teamJerseyStr && (
        (this.state.highlightedPlayers && this.state.highlightedPlayers.includes(teamJerseyStr))
        || (this.detectionHighlightRings && this.detectionHighlightPlayers.includes(teamJerseyStr))
      )

    // Draw gold selection ring if player is selected for telestration
    const isSelectedForTel =
      this.telestration.telActiveLayers['connect'] &&
      this.telestration.selectedPlayers.some(
        (s) => s.team === teamId && s.jersey === player.jersey,
      )
    if (isSelectedForTel) {
      ctx.beginPath()
      ctx.arc(pos.x, pos.y, 14, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(255, 215, 0, 0.9)'
      ctx.lineWidth = 3
      ctx.stroke()
    }

    // Draw gradient ring if player is highlighted (matching workbench style)
    if (isHighlighted) {
      const gradient = ctx.createRadialGradient(pos.x, pos.y, 8, pos.x, pos.y, 25)
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0)')
      gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.2)')
      gradient.addColorStop(0.6, 'rgba(255, 215, 0, 0.6)') // Golden color
      gradient.addColorStop(0.8, 'rgba(255, 215, 0, 0.8)')
      gradient.addColorStop(1, 'rgba(255, 215, 0, 0)')

      ctx.fillStyle = gradient
      ctx.beginPath()
      ctx.arc(pos.x, pos.y, 25, 0, Math.PI * 2)
      ctx.fill()
    }

    // Draw player circle. Ring and number contrast with the kit color —
    // light kits take a dark ring/number (upstream 68cfd4f, ported).
    const contrast = dotContrast(color)
    ctx.beginPath()
    ctx.arc(pos.x, pos.y, 8, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()
    ctx.strokeStyle = contrast.ring
    ctx.lineWidth = 2
    ctx.stroke()

    // Draw jersey number
    ctx.fillStyle = contrast.number
    ctx.font = 'bold 10px Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const jerseyNumber = player.jersey
    ctx.fillText(
      jerseyNumber !== null && jerseyNumber !== 0 ? String(jerseyNumber) : '?',
      pos.x,
      pos.y,
    )

    // Direction arrow: measured against the fix LOOKBACK ago; players
    // below a brisk walk get none (standstill deltas are tracking noise).
    if (player.jersey != null) {
      const prev = this.prevPlayerPos.get(`${teamId}_${player.jersey}`)
      const v = arrowVector(prev, { x: player.x, y: player.y }, this.arrowDtSeconds)
      if (v) {
        // Pitch y is up-positive, canvas y down-positive — negate uy.
        const { shaft, head } = arrowStrokes(
          pos, { x: v.ux, y: -v.uy }, 8, arrowLength(v.speedMs))
        ctx.save()
        ctx.strokeStyle = color
        ctx.globalAlpha = 0.9
        ctx.lineWidth = 2
        ctx.lineCap = 'round'
        ctx.beginPath()
        for (const [a, b] of [shaft, ...head]) {
          ctx.moveTo(a.x, a.y)
          ctx.lineTo(b.x, b.y)
        }
        ctx.stroke()
        ctx.restore()
      }
    }
  }

  /** Draw dashed gold lines connecting detection-highlighted players on
   *  the pitch, mirroring the lines drawn on the video overlay. */
  private drawDetectionHighlightLines(frameData: TrackingFrame): void {
    if (!this.detectionHighlightLines) return
    const ids = this.detectionHighlightPlayers
    if (ids.length < 2 || !frameData.players) return
    const ctx = this.state.ctx!
    const centers: { x: number; y: number }[] = []
    for (const p of frameData.players) {
      if (p.team !== 0 && p.team !== 1) continue
      if (p.jersey == null) continue
      const key = `${p.team}_${p.jersey}`
      if (!ids.includes(key)) continue
      const pos = this.convertToCanvasCoords(p.x, p.y)
      centers.push(pos)
    }
    if (centers.length < 2) return
    ctx.save()
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.8)'
    ctx.lineWidth = 2
    ctx.setLineDash([6, 4])
    for (let i = 0; i < centers.length - 1; i++) {
      ctx.beginPath()
      ctx.moveTo(centers[i]!.x, centers[i]!.y)
      ctx.lineTo(centers[i + 1]!.x, centers[i + 1]!.y)
      ctx.stroke()
    }
    if (centers.length >= 3) {
      ctx.beginPath()
      ctx.moveTo(centers[centers.length - 1]!.x, centers[centers.length - 1]!.y)
      ctx.lineTo(centers[0]!.x, centers[0]!.y)
      ctx.stroke()
    }
    ctx.restore()
  }

  /** Draw ball. */
  private drawBall(ball: NonNullable<TrackingFrame['ball']>): void {
    const ctx = this.state.ctx!
    // Drawn on the pitch even when the feed places it beyond a line: a
    // throw-in or a goal kick is taken from the line, and a ball drifting
    // off the rendered pitch read as a bug (PRI-1926). The data is untouched.
    const onPitch = clampBallToPitch(ball.x, ball.y)
    const pos = this.convertToCanvasCoords(onPitch.x, onPitch.y)

    // Ball shadow if elevated
    if (ball.z > 10) {
      const shadowOffset = (ball.z / 100) * 5
      ctx.beginPath()
      ctx.arc(pos.x, pos.y + shadowOffset, 4, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
      ctx.fill()
    }

    // Ball
    const heightOffset = (ball.z / 100) * 10
    ctx.beginPath()
    ctx.arc(pos.x, pos.y - heightOffset, 5, 0, Math.PI * 2)
    ctx.fillStyle = 'white'
    ctx.fill()
    ctx.strokeStyle = 'black'
    ctx.lineWidth = 1.5
    ctx.stroke()
  }

  /**
   * Draw potential pass arrows from ball holder to potential receivers.
   * DYNAMIC: positions are looked up from the current frame, so arrows follow
   * players during playback.
   */
  private drawPotentialPasses(frameData: TrackingFrame | null): void {
    const { ctx, canvas, potentialPasses, showPassOptions, currentPassMoment } = this.state

    if (!ctx || !canvas || !potentialPasses || !showPassOptions) {
      return
    }

    const ball_holder = potentialPasses.ball_holder
    const potential_passes = potentialPasses.potential_passes

    if (!ball_holder) {
      return
    }

    // Frame for dynamic position lookups: prefer the frame being drawn (passed
    // by drawFrame) over the currentFrameIndex, which lags during playback.
    const currentFrame =
      frameData ||
      (this.state.frames && this.state.currentFrameIndex < this.state.frames.length
        ? this.state.frames[this.state.currentFrameIndex]
        : null)

    // DYNAMIC: Look up ball holder position from current frame by jersey.
    // Falls back to stored position if player not found in current frame.
    let holderPos: { x: number; y: number } | null = null
    if (currentFrame && currentFrame.players) {
      const holderPlayer = currentFrame.players.find(
        (p) => p.jersey === ball_holder.jersey && p.team === ball_holder.team,
      )
      if (holderPlayer) {
        // Tracking data is in cm, convert directly
        holderPos = this.convertToCanvasCoords(holderPlayer.x, holderPlayer.y)
      }
    }
    // Fallback to stored position (meters -> cm)
    if (!holderPos) {
      holderPos = this.convertToCanvasCoords(ball_holder.x * 100, ball_holder.y * 100)
    }

    const MOCK_ACTUAL_PASS_SCORE = 0.76 // Score for the actual pass taken

    // --- Draw actual pass arrow (solid, thicker) ---
    if (currentPassMoment && currentPassMoment.to_player) {
      // Extract receiver jersey from to_player (format: "team1_player10" or similar)
      const toPlayerStr = currentPassMoment.to_player
      let receiverJersey: number | null = null

      const jerseyDigits = toPlayerStr.match(/player(\d+)/i)?.[1]
      if (jerseyDigits !== undefined) {
        receiverJersey = parseInt(jerseyDigits, 10)
      }

      // Find receiver position in current frame (uses dynamic currentFrame from above)
      if (receiverJersey !== null && currentFrame && currentFrame.players) {
        // Find the receiver player (same team as ball holder)
        const receiverPlayer = currentFrame.players.find(
          (p) => p.jersey === receiverJersey && p.team === ball_holder.team,
        )

        if (receiverPlayer) {
          const receiverPos = this.convertToCanvasCoords(receiverPlayer.x, receiverPlayer.y)

          const actualPassScore =
            currentPassMoment.score !== undefined ? currentPassMoment.score : MOCK_ACTUAL_PASS_SCORE

          const style = getPassArrowStyle(actualPassScore)

          // Draw solid line (fixed 3px width for actual pass)
          ctx.save()
          ctx.beginPath()
          ctx.strokeStyle = style.strokeColor
          ctx.lineWidth = 3
          ctx.moveTo(holderPos.x, holderPos.y)
          ctx.lineTo(receiverPos.x, receiverPos.y)
          ctx.stroke()
          ctx.restore()

          // Draw arrowhead
          const angle = Math.atan2(receiverPos.y - holderPos.y, receiverPos.x - holderPos.x)
          const arrowLength = 10 // Fixed size arrowhead

          ctx.save()
          ctx.fillStyle = style.fillColor
          ctx.beginPath()
          ctx.moveTo(receiverPos.x, receiverPos.y)
          ctx.lineTo(
            receiverPos.x - arrowLength * Math.cos(angle - Math.PI / 6),
            receiverPos.y - arrowLength * Math.sin(angle - Math.PI / 6),
          )
          ctx.lineTo(
            receiverPos.x - arrowLength * Math.cos(angle + Math.PI / 6),
            receiverPos.y - arrowLength * Math.sin(angle + Math.PI / 6),
          )
          ctx.closePath()
          ctx.fill()
          ctx.restore()
        }
      }
    }

    // --- Draw potential passes (dashed, performance-colored) ---
    if (!potential_passes || potential_passes.length === 0) {
      return
    }

    // DYNAMIC: Look up target positions from current frame so arrows follow players
    potential_passes.forEach((pass, idx) => {
      const target = pass.target_player
      if (!target) return

      let targetPos: { x: number; y: number } | null = null
      if (currentFrame && currentFrame.players) {
        const targetPlayer = currentFrame.players.find(
          (p) => p.jersey === target.jersey && p.team === ball_holder.team,
        )
        if (targetPlayer) {
          targetPos = this.convertToCanvasCoords(targetPlayer.x, targetPlayer.y)
        }
      }
      // Fallback to stored position (meters -> cm)
      if (!targetPos) {
        targetPos = this.convertToCanvasCoords(target.x * 100, target.y * 100)
      }

      const passScore =
        pass.score ?? mockPassScore(idx)

      const style = getPassArrowStyle(passScore)

      // Draw dashed line from holder to target
      ctx.save()
      ctx.beginPath()
      ctx.setLineDash([8, 4]) // Dashed pattern
      ctx.strokeStyle = style.strokeColor
      ctx.lineWidth = style.lineWidth
      ctx.moveTo(holderPos.x, holderPos.y)
      ctx.lineTo(targetPos.x, targetPos.y)
      ctx.stroke()
      ctx.setLineDash([]) // Reset dash pattern
      ctx.restore()

      // Draw arrowhead at target end
      const angle = Math.atan2(targetPos.y - holderPos.y, targetPos.x - holderPos.x)

      ctx.save()
      ctx.fillStyle = style.fillColor
      ctx.beginPath()
      ctx.moveTo(targetPos.x, targetPos.y)
      ctx.lineTo(
        targetPos.x - style.arrowLength * Math.cos(angle - Math.PI / 6),
        targetPos.y - style.arrowLength * Math.sin(angle - Math.PI / 6),
      )
      ctx.lineTo(
        targetPos.x - style.arrowLength * Math.cos(angle + Math.PI / 6),
        targetPos.y - style.arrowLength * Math.sin(angle + Math.PI / 6),
      )
      ctx.closePath()
      ctx.fill()
      ctx.restore()

      // Draw small circle at target player position (using same performance color)
      ctx.save()
      ctx.beginPath()
      ctx.arc(targetPos.x, targetPos.y, 12, 0, Math.PI * 2)
      ctx.strokeStyle = style.strokeColor
      ctx.lineWidth = style.lineWidth * 0.8 // Slightly thinner circle
      ctx.stroke()
      ctx.restore()
    })

    // Draw highlight around ball holder
    ctx.save()
    ctx.beginPath()
    ctx.arc(holderPos.x, holderPos.y, 14, 0, Math.PI * 2)
    ctx.strokeStyle = 'rgba(251, 191, 36, 0.9)' // Gold
    ctx.lineWidth = 3
    ctx.stroke()
    ctx.restore()
  }

  // -------------------------------------------------------------------------
  // Playback loop (template startCanvasPlayback / stopCanvasPlayback)
  // -------------------------------------------------------------------------

  startCanvasPlayback(): void {
    if (this.state.isPlaying) return

    this.state.isPlaying = true
    this.state.lastDrawnFrame = -1 // Track last drawn frame to avoid redundant draws
    this.state.lastSyncLog = 0
    const video = this.videoEl()
    // Not playing without a video: leaving isPlaying set here wedged every
    // later start behind the early return above.
    if (!video) { this.state.isPlaying = false; return }

    const animate = (currentTime: number) => {
      if (!this.state.isPlaying || this.destroyed) return

      // VIDEO-DRIVEN SYNC: Query video position as single source of truth
      const videoFrame = videoTimeToFrame(this.getSync(), video.currentTime)

      // Update match clock to stay synced with video
      this.updateMatchClock(videoFrame)

      // Update pressing indicator if a pressing moment is active (Pass 12)
      updatePressingIndicatorForFrame(videoFrame)

      // Optional debug logging (every 2 seconds)
      if (this.state.debugSync && currentTime - this.state.lastSyncLog > 2000) {
        this.state.lastSyncLog = currentTime
      }

      // Check if we have this frame in buffer
      const frameStart = this.state.currentFrame
      const frameEnd = frameStart + (this.state.frames ? this.state.frames.length : 0)
      const isFrameInBuffer =
        this.state.frames &&
        this.state.frames.length > 0 &&
        videoFrame >= frameStart &&
        videoFrame < frameEnd

      if (isFrameInBuffer) {
        // Frame is in buffer - draw it if we haven't already
        const bufferIndex = videoFrame - frameStart

        if (videoFrame !== this.state.lastDrawnFrame) {
          try {
            // Update the index BEFORE drawing so anything that reads
            // currentFrameIndex during drawFrame (overlays) sees the frame
            // being drawn, not the previous one
            this.state.currentFrameIndex = bufferIndex
            this.redrawCurrentFrame()
            this.state.lastDrawnFrame = videoFrame
          } catch (err) {
            console.error('Error in drawFrame:', err)
          }
        }
      } else {
        // Frame not in buffer
        if (!this.state.isLoadingFrames) {
          // Load frames starting from current video position
          void this.loadFrameData(videoFrame, true)
        }
        // Keep displaying last drawn frame while loading
      }

      // ALWAYS continue the loop (unless explicitly stopped via isPlaying flag)
      this.state.animationId = requestAnimationFrame(animate)
    }

    this.state.animationId = requestAnimationFrame(animate)
  }

  stopCanvasPlayback(): void {
    this.state.isPlaying = false
    if (this.state.animationId) {
      cancelAnimationFrame(this.state.animationId)
      this.state.animationId = null
    }
  }

  // -------------------------------------------------------------------------
  // Video event handlers, canvas halves (template handleVideo*, ~5593)
  // -------------------------------------------------------------------------

  handleVideoPlay(video: HTMLVideoElement): void {
    this.video = video
    this.videoPlaying = true

    // Note: Don't clear potentialPasses here - let the 5-second timer in
    // updatePassPotentialIndicator() handle cleanup for consistent timing

    const currentFrame = videoTimeToFrame(this.getSync(), video.currentTime)

    if (!this.state.canvas) {
      this.initializeCanvas()
    }

    // Check if we already have frames that include the current position
    const frameStart = this.state.currentFrame
    const frameEnd = frameStart + (this.state.frames ? this.state.frames.length : 0)
    const isFrameInBuffer =
      this.state.frames &&
      this.state.frames.length > 0 &&
      currentFrame >= frameStart &&
      currentFrame < frameEnd

    if (isFrameInBuffer) {
      // Update index to match current video position
      this.state.currentFrameIndex = currentFrame - frameStart
      if (!this.state.isPlaying) {
        this.startCanvasPlayback()
      }
    } else if (!this.state.isLoadingFrames) {
      void this.loadFrameData(currentFrame, true)
    } else {
      // Queue playback to start after current load completes
      this.state.pendingPlayback = true
    }
  }

  handleVideoPause(): void {
    this.videoPlaying = false
    this.stopCanvasPlayback()
  }

  async handleVideoSeeked(video: HTMLVideoElement): Promise<void> {
    this.video = video
    const currentFrame = videoTimeToFrame(this.getSync(), video.currentTime)

    // Ensure the WASM tracking cache has this region so the playback loop
    // doesn't fall back to the JSON endpoint while the background loader
    // is still working through earlier chunks.
    void this.trackingCache.prioritizeRange(currentFrame)

    // Only stop canvas playback if video is actually paused.
    // If video is playing, let it continue (handleVideoPlay manages loading).
    if (video.paused) {
      this.stopCanvasPlayback()

      // Use unified loading to ensure spatial control and formations are loaded
      try {
        await this.loadAllDataUnified(currentFrame, false)
      } catch (error) {
        console.error('[handleVideoSeeked] Error loading data:', error)
      }
    }
  }

  /** Canvas half of the template's loadedmetadata handler (~5510-5528). */
  handleLoadedMetadata(deepLinkFrame: number | null): void {
    if (!this.state.canvas) {
      this.initializeCanvas()
    }
    if (deepLinkFrame != null) {
      void this.loadFrameData(deepLinkFrame, false)
    } else {
      void this.loadFrameData(0, false)
    }
  }

  // -------------------------------------------------------------------------
  // playMoment canvas half (template playMoment, ~3913-4002)
  // -------------------------------------------------------------------------

  /**
   * Player highlighting + pass potential for the moment about to play. The
   * video seek/play/unified-load sequence runs in VideoPlayer around this.
   */
  preparePlayMoment(moment: Moment | undefined): void {
    if (!moment) return

    // Extract players for highlighting with team information
    const highlightedPlayers: string[] = []

    if (moment.from_player) {
      const teamJersey = extractTeamAndJersey(moment.from_player)
      if (teamJersey) highlightedPlayers.push(teamJersey)
    }
    if (moment.to_player) {
      const teamJersey = extractTeamAndJersey(moment.to_player)
      if (teamJersey) highlightedPlayers.push(teamJersey)
    }
    if (moment.player) {
      const teamJersey = extractTeamAndJersey(moment.player)
      if (teamJersey && !highlightedPlayers.includes(teamJersey)) {
        highlightedPlayers.push(teamJersey)
      }
    }

    this.setHighlightedPlayers(highlightedPlayers, 5000)

    // Show pressing indicator for pressing moments (Pass 12)
    updatePressingIndicator(moment, this.opts.getPressTypes?.() ?? [], this.getSync().fps)

    // Load potential passes from pre-loaded data for passing moments.
    const momentType = moment.type || ''
    const frame = moment.frame || moment.start_frame || 0

    if (momentType === 'progressive_pass' || momentType === 'supportive_pass') {
      const potentialPasses = this.passPotentialData[frame]
      if (potentialPasses) {
        this.state.potentialPasses = potentialPasses
        this.state.currentPassMoment = moment // Store moment for actual pass arrow
        this.state.lastPassPotentialFrame = frame
        // Show pass potential indicator with score rings
        this.updatePassPotentialIndicator(moment, potentialPasses)
      } else {
        this.state.potentialPasses = null
        this.state.currentPassMoment = null
        this.state.lastPassPotentialFrame = null
        this.hidePassPotentialIndicator()
      }
    } else {
      this.state.potentialPasses = null
      this.state.currentPassMoment = null
      this.state.lastPassPotentialFrame = null
      this.hidePassPotentialIndicator()
    }
  }

  /** Set the detection-highlight players (no timeout — persists until the
   *  detection highlight is cleared). Called from telestration every frame. */
  setDetectionHighlightPlayers(
    players: string[], rings = true, lines = true,
  ): void {
    const changed = players.length !== this.detectionHighlightPlayers.length
      || players.some((p, i) => p !== this.detectionHighlightPlayers[i])
      || rings !== this.detectionHighlightRings
      || lines !== this.detectionHighlightLines
    this.detectionHighlightPlayers = players
    this.detectionHighlightRings = rings
    this.detectionHighlightLines = lines
    // Only trigger a pitch redraw when the set actually changed, not on
    // every detection-box repaint (which is every video frame).
    if (changed) this.redrawCurrentFrame()
  }

  /** Set highlighted players with optional timeout (template ~4962). */
  setHighlightedPlayers(players: string[], duration: number | null = null): void {
    // Clear any existing timeout
    if (this.state.highlightTimeout) {
      clearTimeout(this.state.highlightTimeout)
      this.state.highlightTimeout = null
    }

    this.state.highlightedPlayers = players || []

    // DON'T redraw immediately during moment selection to avoid race condition.
    // The frame loading flow (loadFrameData -> processFetchedFrames -> drawFrame)
    // will handle it — prevents highlighting the wrong frame when seeking.

    // Set timeout to clear highlights if duration specified
    if (duration) {
      this.state.highlightTimeout = setTimeout(() => {
        this.state.highlightedPlayers = []
        // Redraw to remove highlights
        if (
          this.state.frames &&
          this.state.frames.length > 0 &&
          this.state.currentFrameIndex < this.state.frames.length
        ) {
          this.redrawCurrentFrame()
        }
      }, duration)
    }
  }

  // -------------------------------------------------------------------------
  // Pass potential indicator (template ~5209-5333)
  // -------------------------------------------------------------------------

  updatePassPotentialIndicator(moment: Moment | null, potentialPasses: PassPotentialData): void {
    const indicator = document.getElementById('pass-potential-indicator')
    const pressingIndicator = document.getElementById('pressing-indicator')
    if (!indicator) return

    // Clear any existing auto-hide timer
    if (this.passPotentialIndicatorTimer) {
      clearTimeout(this.passPotentialIndicatorTimer)
      this.passPotentialIndicatorTimer = null
    }

    // Only show for pass moments when pass options toggle is active.
    // 'possession' type comes from pass_potential.parquet during playback.
    const momentType = moment?.type || potentialPasses?.moment_type
    const isPassMoment =
      momentType === 'progressive_pass' ||
      momentType === 'supportive_pass' ||
      momentType === 'possession'
    const hasPassOptions =
      potentialPasses && potentialPasses.potential_passes && potentialPasses.potential_passes.length > 0

    if (!isPassMoment || !hasPassOptions || !this.state.showPassOptions) {
      this.hidePassPotentialIndicator()
      return
    }

    // Hide pressing indicator (mutually exclusive - in vs out of possession)
    if (pressingIndicator) {
      pressingIndicator.style.display = 'none'
    }

    // The first two options: the server sends each frame best first
    const passes = potentialPasses.potential_passes!.slice(0, 2)

    // Get ball holder jersey for label
    const holderJersey = potentialPasses.ball_holder?.jersey ?? '?'

    // The two option slots were the same fifteen lines twice, differing only
    // in the element id and the index. One loop, so a change to how an option
    // renders cannot land in slot 1 and be forgotten in slot 2.
    for (const slot of [1, 2]) {
      const row = document.getElementById(`pass-option-${slot}`)
      if (!row) continue
      const pass = passes[slot - 1]
      if (!pass) {
        row.style.display = 'none'
        continue
      }
      const targetJersey = pass.target_player?.jersey ?? '?'
      const subhead = document.getElementById(`pass-option-${slot}-subhead`)
      if (subhead) subhead.textContent = `#${holderJersey} → #${targetJersey}`
      ScoreRing.render(`pass-option-${slot}-ring`, pass.score ?? mockPassScore(slot - 1), {
        variant: 'performance',
        size: 'sm',
      })
      row.style.display = 'flex'
    }

    // Show the indicator
    indicator.style.display = 'flex'

    // Set auto-hide timer (5 seconds)
    this.passPotentialIndicatorTimer = setTimeout(() => {
      this.hidePassPotentialIndicator()
    }, 5000)
  }

  /**
   * Hide pass potential indicator and reset timer. Intentionally does NOT
   * clear state.potentialPasses (see the template's long comment: clearing it
   * here broke arrows during playback for moment_type='possession').
   */
  hidePassPotentialIndicator(): void {
    const indicator = document.getElementById('pass-potential-indicator')
    if (indicator) {
      indicator.style.display = 'none'
    }

    if (this.passPotentialIndicatorTimer) {
      clearTimeout(this.passPotentialIndicatorTimer)
      this.passPotentialIndicatorTimer = null
    }
  }

  // -------------------------------------------------------------------------
  // Data loads owned by this slice
  // -------------------------------------------------------------------------

  /** Template loadPassPotentialData (~3140). A 5xx, a refused payload or
   *  a network error is retried (PASS_POTENTIAL_RETRY_MS) before the load
   *  is called failed: one blip must not read as "this game has no pass
   *  data" for the rest of the session. So are a 408 and a 429. Any other
   *  4xx (an expired session) fails at once — asking again cannot change
   *  the answer. */
  async loadPassPotentialData(attempt = 0): Promise<void> {
    let status: PassPotentialStatus
    let retryable = true
    try {
      const response = await fetch(`/api/v1/game-match-summary/pass-potential/${this.gameCode}`, {
        credentials: 'same-origin',
      })
      // 408 and 429 are blips too: a timeout and a rate limit pass
      retryable = response.ok || response.status >= 500
        || response.status === 408 || response.status === 429
      const data = response.status === 404 ? null
        : response.ok ? await response.json() : undefined
      if (data === null) {
        status = 'none'
      } else if (data?.success && Array.isArray(data.moments)) {
        // Index by frame for quick lookup
        this.passPotentialData = {}
        for (const moment of data.moments as PassPotentialData[]) {
          this.passPotentialData[moment.frame] = moment
        }
        status = data.moments.length ? 'ready' : 'none'
      } else {
        status = 'failed'
      }
    } catch {
      status = 'failed'
    }
    if (this.destroyed) return
    const wait = PASS_POTENTIAL_RETRY_MS[attempt]
    if (status === 'failed' && retryable && wait !== undefined) {
      setTimeout(() => { if (!this.destroyed) void this.loadPassPotentialData(attempt + 1) }, wait)
      return
    }
    this.passPotentialStatus = status
    this.onPassPotentialStatus?.(status)
  }

  /** Template trackingCache.initialize call (loadGameData Promise.all). */
  async initializeTrackingCache(): Promise<void> {
    try {
      // total_frames rides on gameData when it has loaded; without it the
      // cache keeps its 90-minute default and seeks cover the tail.
      await this.trackingCache.initialize(
        this.gameCode, this.opts.getGameData()?.total_frames)
    } catch (error) {
      console.error('Failed to initialize tracking cache:', error)
      console.warn('Tracking queries will fall back to API')
    }
  }

  // -------------------------------------------------------------------------
  // Layer toggles (template toggleSpatialControl / toggleFormations /
  // togglePassOptions, ~6266-6540)
  // -------------------------------------------------------------------------

  async toggleSpatialControl(enabled: boolean): Promise<void> {
    await this.spatialControl.toggle(enabled)
    trackEvent('Spatial Control Toggled', {
      game_code: this.gameCode,
      // Template read spatialControl.isActive, which never existed — the
      // payload was always false. Kept for analytics-series continuity.
      enabled: false,
    })
  }

  /** Trailing-window heatmap. `team` is 0/1 (matches org_team_id) or null
      for both; the caller passes the side the page is oriented to. */
  toggleHeatMap(enabled: boolean, team: number | null): void {
    this.heatMap.enabled = enabled
    this.heatMap.team = team
    this.heatMap.invalidate()
    trackEvent('Heat Map Toggled', {
      game_code: this.gameCode,
      enabled,
    })
    if (this.state.frames?.length && this.state.currentFrameIndex < this.state.frames.length) {
      this.redrawCurrentFrame()
    }
  }

  /** Ball trail, coloured by possession. Loads the possession model on first
      enable; the trail draws neutral grey if that model does not exist. */
  toggleBallTrail(enabled: boolean): void {
    this.ballTrail.enabled = enabled
    trackEvent('Ball Trail Toggled', { game_code: this.gameCode, enabled })
    const redraw = () => {
      if (this.state.frames?.length && this.state.currentFrameIndex < this.state.frames.length) {
        this.redrawCurrentFrame()
      }
    }
    if (enabled) void this.ballTrail.ensureLoaded().then(redraw)
    else redraw()
  }

  /** Player trails. Narrows to highlighted players when there are any. */
  togglePlayerTrails(enabled: boolean): void {
    this.playerTrails.enabled = enabled
    trackEvent('Player Trails Toggled', { game_code: this.gameCode, enabled })
    if (this.state.frames?.length && this.state.currentFrameIndex < this.state.frames.length) {
      this.redrawCurrentFrame()
    }
  }

  /** Speed trails: players and ball on the clear/green/yellow/red ramp. */
  toggleSpeedTrails(enabled: boolean): void {
    this.speedTrails.enabled = enabled
    trackEvent('Speed Trails Toggled', { game_code: this.gameCode, enabled })
    if (this.state.frames?.length && this.state.currentFrameIndex < this.state.frames.length) {
      this.redrawCurrentFrame()
    }
  }

  /** Defensive block and the gap between the lines, for `team`. */
  toggleBlockLines(enabled: boolean, team: number): void {
    this.blockLines.enabled = enabled
    this.blockTeam = team
    trackEvent('Block Lines Toggled', { game_code: this.gameCode, enabled })
    if (this.state.frames?.length && this.state.currentFrameIndex < this.state.frames.length) {
      this.redrawCurrentFrame()
    }
  }

  async toggleFormations(): Promise<void> {
    await this.formations.toggle()
    trackEvent('Formations Toggled', {
      game_code: this.gameCode,
      // Same nonexistent-.isActive read as above — always false in the template.
      enabled: false,
    })
  }

  togglePassOptions(): void {
    this.state.showPassOptions = !this.state.showPassOptions
    trackEvent('Pass Options Toggled', {
      game_code: this.gameCode,
      enabled: this.state.showPassOptions,
    })

    // Hide pass potential indicator when toggle is off
    if (!this.state.showPassOptions) {
      this.hidePassPotentialIndicator()
    }

    // Redraw current frame to show/hide pass options
    if (
      this.state.frames &&
      this.state.frames.length > 0 &&
      this.state.currentFrameIndex < this.state.frames.length
    ) {
      this.redrawCurrentFrame()
    }
  }

  /** Phase toggle side effect (template out-possession branch, ~5813). */
  disablePassOptionsForOutOfPossession(): void {
    this.state.showPassOptions = false
    this.hidePassPotentialIndicator()
  }

  // -------------------------------------------------------------------------
  // Team colors (template initTeamColorPickers applyTeamColor/saveTeamColors)
  // -------------------------------------------------------------------------

  applyTeamColor(teamIndex: number, hex: string): void {
    const root = document.documentElement
    const rgb = hexToRgb(hex)
    const deep = darken(rgb, 0.4)
    const prefix = teamIndex === 0 ? 'blue' : 'red'

    root.style.setProperty(`--spatial-control-${prefix}`, hex)
    root.style.setProperty(`--spatial-control-${prefix}-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`)
    root.style.setProperty(
      `--spatial-control-${prefix}-deep`,
      `#${deep.r.toString(16).padStart(2, '0')}${deep.g.toString(16).padStart(2, '0')}${deep.b.toString(16).padStart(2, '0')}`,
    )
    root.style.setProperty(`--spatial-control-${prefix}-deep-rgb`, `${deep.r}, ${deep.g}, ${deep.b}`)
    // Keep the static legend vars in sync (team 1 = home, team 0 = away)
    root.style.setProperty(teamIndex === 1 ? '--color-home-team' : '--color-away-team', hex)

    // Invalidate cached colors so they get re-read from CSS
    this._teamColors = null
    this.spatialControl._colors = null
    this.spatialControl.renderedCache.clear()

    // Redraw current frame
    if (this.state.lastFrameData) {
      this.drawFrame(this.state.lastFrameData)
    }
  }

  /**
   * Persist manual picks to the GameFile (org-wide). The caller updates its
   * own copy of gameData colors on success, as the template did.
   */
  async saveTeamColors(colors: { home?: string; away?: string }): Promise<boolean> {
    try {
      const resp = await fetch(`/api/v1/game-match-summary/game/${this.gameCode}/team-colors`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(colors),
      })
      const data = await resp.json()
      if (data.success) {
        return true
      }
      console.error('Failed to save team colors:', data.error)
      return false
    } catch (e) {
      console.error('Failed to save team colors:', e)
      return false
    }
  }
}
